import { useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";

/**
 * Confirmation step rather than redeeming the token on arrival.
 *
 * Mail providers and security scanners prefetch links. If loading this page consumed the
 * token, a scanner could burn it before the owner ever clicked, and the owner would see
 * "expired" for no reason. One button press removes that failure mode entirely.
 */
export default function VerifyMagicLinkPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!token) return <Navigate to="/signin?error=expired" replace />;

  async function confirm() {
    setBusy(true);
    try {
      const response = await fetch("/api/auth/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      // The endpoint redirects to / on success and to /signin?error=expired on failure.
      // A full page load is wanted here so the new session cookie is picked up everywhere.
      if (response.redirected) {
        window.location.href = response.url;
        return;
      }
      window.location.href = "/";
    } catch {
      setBusy(false);
      setFailed(true);
    }
  }

  return (
    <main className="app-shell auth-shell">
      <section className="empty-state auth-card">
        <div className="empty-stamp">✉</div>
        <h1>Confirm sign in</h1>
        <p>You asked for a sign-in link for this postcard archive. Confirm to continue.</p>

        {failed && (
          <p className="auth-note auth-note-alert" role="alert">
            That did not work. Request a fresh link and try again.
          </p>
        )}

        <div className="auth-actions">
          <button className="primary-button" type="button" onClick={confirm} disabled={busy}>
            {busy ? "Signing in…" : "Confirm sign in"}
          </button>
        </div>

        <p className="auth-back">
          <Link to="/">← Back to the archive</Link>
        </p>
      </section>
    </main>
  );
}
